/**
 * TeamMembersCard Component
 *
 * Manager view - displays team members with their objectives
 */

import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip, Avatar } from '@mui/material';
import { Groups, Visibility, Add } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { EmptyState } from '../shared/EmptyState';
import { getScoreColor } from '../utils/scoreUtils';

export const TeamMembersCard: React.FC = () => {
    const { isDarkMode, data, permissions, sections, dialogs, filteredObjectives } = useOKRDashboardContext();
    const { employees, userContext } = data;
    const { expandedSections, toggleSection } = sections;

    // Only show for managers
    if (!permissions.isManager) {
        return null;
    }

    // Filter employees to current manager's department
    const deptEmployees = employees.filter(
        (emp) => Number(emp.department_id || emp.dept_id) === Number(userContext.dept_id)
    );

    // Extract all individual objectives from team objectives (nested structure)
    // This includes all team members' objectives, not just the logged-in user's
    const allIndividualObjectives = (filteredObjectives.myDeptTeam ?? filteredObjectives.team).flatMap(
        (teamObj) => teamObj.individualObjectives || []
    );

    return (
        <CollapsibleCard
            icon={<Groups sx={{ color: '#fff', fontSize: 24 }} />}
            iconBgGradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            title="Team Members"
            subtitle="Your team's objectives and progress"
            expanded={expandedSections.teamMembers}
            onToggle={() => toggleSection('teamMembers')}
            isDarkMode={isDarkMode}
            count={deptEmployees.length}
        >
            {deptEmployees.length === 0 ? (
                <EmptyState
                    title="No Team Members"
                    description="No employees found in your department"
                    isDarkMode={isDarkMode}
                    compact
                />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {deptEmployees.map((employee) => {
                        // Find objectives for this employee from the nested team objectives
                        // Compare as strings to handle type mismatches
                        const employeeObjectives = allIndividualObjectives.filter(
                            (obj) => String(obj.employee_id) === String(employee.id)
                        );
                        const avgScore = employeeObjectives.length > 0
                            ? employeeObjectives.reduce((sum, obj) => sum + (parseFloat(String(obj.score)) || 0), 0) / employeeObjectives.length
                            : 0;
                        const scoreColor = getScoreColor(avgScore);

                        return (
                            <Box
                                key={employee.id}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                                    border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: isDarkMode ? '#1e293b80' : '#f1f5f9',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                bgcolor: `${scoreColor}20`,
                                                color: scoreColor,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {(employee.name || employee.firstName || 'U').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.95rem',
                                                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                }}
                                            >
                                                {employee.name || employee.firstName || 'Unknown'}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: isDarkMode ? '#64748b' : '#94a3b8',
                                                }}
                                            >
                                                {employeeObjectives.length} objective{employeeObjectives.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                bgcolor: scoreColor,
                                                color: '#fff',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                boxShadow: `0 2px 6px ${scoreColor}40`,
                                            }}
                                        >
                                            {Math.round(avgScore)}%
                                        </Box>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => dialogs.openMemberView(employee)}
                                                sx={{
                                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                                    '&:hover': {
                                                        bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                    },
                                                }}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {permissions.canAssignMilestones && (
                                            <Tooltip title="Assign Task">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => dialogs.openAddMilestoneDialog(undefined, employee.id)}
                                                    sx={{
                                                        color: '#6366f1',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                                                        },
                                                    }}
                                                >
                                                    <Add fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </CollapsibleCard>
    );
};

export default TeamMembersCard;
